import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "../components/ui/sonner";
import { CheckCircle2, XCircle, UserPlus, Globe2, Trash2 } from "lucide-react";

const ROLES = ["ADMIN", "DIRECTION_SECURITE", "AUDITEUR", "RESPONSABLE_SITE", "LECTEUR"];
const GLOBAL_ROLES = ["ADMIN", "DIRECTION_SECURITE"];

export default function Admin() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", nom: "", prenom: "", role: "LECTEUR", site_id: "" });
  const [userToDelete, setUserToDelete] = useState(null);

  const load = () => api.get("/admin/status").then((res) => setStatus(res.data));
  const loadUsers = () => api.get("/utilisateurs").then((res) => setUsers(res.data));

  useEffect(() => {
    load();
    loadUsers();
    api.get("/sites").then((res) => setSites(res.data));
  }, []);

  const run = async (fn, label) => {
    setLoading(true);
    try {
      const res = await fn();
      toast.success(`${label}: ${JSON.stringify(res.data).slice(0, 120)}`);
      load();
    } catch (e) {
      toast.error(`${label} a échoué: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, payload) => {
    try {
      await api.patch(`/utilisateurs/${userId}`, payload);
      toast.success("Utilisateur mis à jour");
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la mise à jour");
    }
  };

  const changeUserRole = (u, role) => {
    const payload = { role };
    if (GLOBAL_ROLES.includes(role)) payload.site_id = "ALL";
    else if (u.site_id === "ALL") payload.site_id = "";
    updateUser(u.user_id, payload);
  };

  const setNewUserRole = (role) => {
    setNewUser((f) => ({ ...f, role, site_id: GLOBAL_ROLES.includes(role) ? "ALL" : (f.site_id === "ALL" ? "" : f.site_id) }));
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/utilisateurs/${userToDelete.user_id}`);
      toast.success("Utilisateur supprimé définitivement");
      setUserToDelete(null);
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la suppression");
      setUserToDelete(null);
    }
  };

  const createUser = async () => {
    try {
      await api.post("/utilisateurs", newUser);
      toast.success("Utilisateur ajouté");
      setNewUserOpen(false);
      setNewUser({ email: "", nom: "", prenom: "", role: "LECTEUR", site_id: "" });
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la création");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5" data-testid="admin-page">
      <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Configuration Système</h1>

      {status && (
        <Card data-testid="admin-status-card">
          <CardHeader><CardTitle>État de la connexion</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              {status.sheets_connected ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
              Google Sheets : {status.sheets_connected ? "connecté" : `erreur (${status.sheets_error})`}
            </div>
            <div className="flex items-center gap-2">
              {status.drive_connected ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
              Google Drive : {status.drive_connected ? "connecté" : `erreur (${status.drive_error})`}
            </div>
            <div className="flex items-center gap-2">
              {status.referentiel_valid ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
              Référentiel : {status.referentiel_valid ? "chargé et valide" : "non valide"} — Total: {status.referentiel_total} (attendu 62), Minimum: {status.referentiel_minimum} (attendu 43), World Class: {status.referentiel_world_class} (attendu 19)
            </div>
            <div>Sites: {status.sites_count} · Campagnes: {status.campagnes_count}</div>
            {status.missing_tabs?.length > 0 && <div className="text-amber-600">Onglets manquants: {status.missing_tabs.join(", ")}</div>}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Actions d'initialisation</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button data-testid="admin-test-sheets-button" variant="outline" onClick={() => run(() => api.get("/admin/status"), "Test Google Sheets")} disabled={loading}>Tester Google Sheets</Button>
          <Button data-testid="admin-test-drive-button" variant="outline" onClick={() => run(() => api.get("/admin/status"), "Test Google Drive")} disabled={loading}>Tester Google Drive</Button>
          <Button data-testid="admin-init-tabs-button" onClick={() => run(() => api.post("/admin/init-tabs"), "Initialiser les onglets")} disabled={loading}>Initialiser les onglets</Button>
          <Button data-testid="admin-init-drive-button" onClick={() => run(() => api.post("/admin/init-drive"), "Initialiser Drive")} disabled={loading}>Initialiser l'arborescence Drive</Button>
          <Button data-testid="admin-import-referentiel-button" onClick={() => run(() => api.post("/admin/import-referentiel"), "Importer le référentiel")} disabled={loading}>Importer le référentiel (62)</Button>
          <Button data-testid="admin-seed-data-button" onClick={() => run(() => api.post("/admin/seed-data"), "Semer campagnes/sites")} disabled={loading}>Créer campagnes &amp; sites de base</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Import évaluations 2024</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-2">Utilisez la page « Historique » pour glisser-déposer les fichiers XLSX 2024 par site.</p>
          <Badge variant="outline">Onglet "2 - MoC LMA Unit EI Self-Assess" toujours ignoré à l'import</Badge>
        </CardContent>
      </Card>

      <Card data-testid="admin-users-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Gestion des utilisateurs &amp; rôles</CardTitle>
          <Button size="sm" data-testid="admin-add-user-button" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => setNewUserOpen(true)}>
            <UserPlus className="h-4 w-4" /> Ajouter
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {users.map((u) => (
            <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="flex items-center gap-2 flex-wrap border-b py-2 last:border-0 text-sm">
              <div className="min-w-[160px] flex-1">
                <div className="font-medium truncate">{u.prenom} {u.nom}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
              </div>
              <Select value={u.role} onValueChange={(v) => changeUserRole(u, v)}>
                <SelectTrigger data-testid={`user-role-select-${u.user_id}`} className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              {GLOBAL_ROLES.includes(u.role) ? (
                <div data-testid={`user-site-global-${u.user_id}`} className="w-44 flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-700 rounded-md px-2 py-2 bg-emerald-50 dark:bg-emerald-950/30">
                  <Globe2 className="h-3.5 w-3.5" /> Tous les sites
                </div>
              ) : (
                <Select value={u.site_id || "none"} onValueChange={(v) => updateUser(u.user_id, { site_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid={`user-site-select-${u.user_id}`} className="w-44"><SelectValue placeholder="Site" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucun site —</SelectItem>
                    {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.nom_site}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-1.5">
                <Switch
                  data-testid={`user-active-switch-${u.user_id}`}
                  checked={u.actif === "TRUE"}
                  onCheckedChange={(v) => updateUser(u.user_id, { actif: v ? "TRUE" : "FALSE" })}
                />
                <span className="text-xs text-muted-foreground">{u.actif === "TRUE" ? "Actif" : "Inactif"}</span>
              </div>
              <Button
                size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                data-testid={`user-delete-button-${u.user_id}`} onClick={() => setUserToDelete(u)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {users.length === 0 && <p className="text-sm text-muted-foreground">Aucun utilisateur pour le moment.</p>}
        </CardContent>
      </Card>

      <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
        <DialogContent data-testid="new-user-modal">
          <DialogHeader><DialogTitle>Ajouter un utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Email professionnel" value={newUser.email} onChange={(e) => setNewUser((f) => ({ ...f, email: e.target.value }))} data-testid="new-user-email-input" />
            <div className="flex gap-2">
              <Input placeholder="Prénom" value={newUser.prenom} onChange={(e) => setNewUser((f) => ({ ...f, prenom: e.target.value }))} data-testid="new-user-prenom-input" />
              <Input placeholder="Nom" value={newUser.nom} onChange={(e) => setNewUser((f) => ({ ...f, nom: e.target.value }))} data-testid="new-user-nom-input" />
            </div>
            <Select value={newUser.role} onValueChange={setNewUserRole}>
              <SelectTrigger data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            {GLOBAL_ROLES.includes(newUser.role) ? (
              <div data-testid="new-user-site-global" className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-700 rounded-md px-3 py-2 bg-emerald-50 dark:bg-emerald-950/30">
                <Globe2 className="h-4 w-4" /> Accès à tous les sites (rôle national)
              </div>
            ) : (
              <Select value={newUser.site_id || "none"} onValueChange={(v) => setNewUser((f) => ({ ...f, site_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="new-user-site-select"><SelectValue placeholder="Site (optionnel)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucun site —</SelectItem>
                  {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.nom_site}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewUserOpen(false)}>Annuler</Button>
            <Button data-testid="new-user-save-button" onClick={createUser} disabled={!newUser.email}>Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent data-testid="delete-user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              {userToDelete && (
                <>Cette action supprime <strong>{userToDelete.prenom} {userToDelete.nom}</strong> ({userToDelete.email}) de la liste des utilisateurs. Cette opération est irréversible, contrairement à la désactivation.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel-button">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-user-confirm-button" className="bg-rose-600 hover:bg-rose-700" onClick={confirmDeleteUser}>
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
